interface IdealProfile {
  id: string;
  name: string;
  dominance_min: number;
  dominance_max: number;
  extraversion_min: number;
  extraversion_max: number;
  patience_min: number;
  patience_max: number;
  formality_min: number;
  formality_max: number;
  objectivity_min: number;
  objectivity_max: number;
}

interface SubmissionScores {
  aPercentile: number;
  bPercentile: number;
  cPercentile: number;
  dPercentile: number;
  ePercentile: number;
}

export const calculateProfileMatch = (scores: SubmissionScores, idealProfile: IdealProfile): number => {
  // Use min values as the target percentiles (since they're now the same as max values)
  const dominanceTarget = idealProfile.dominance_min;
  const extraversionTarget = idealProfile.extraversion_min;
  const patienceTarget = idealProfile.patience_min;
  const formalityTarget = idealProfile.formality_min;
  const objectivityTarget = idealProfile.objectivity_min;

  // Calculate absolute differences for each trait
  const dominanceDiff = Math.abs(scores.aPercentile - dominanceTarget);
  const extraversionDiff = Math.abs(scores.bPercentile - extraversionTarget);
  const patienceDiff = Math.abs(scores.cPercentile - patienceTarget);
  const formalityDiff = Math.abs(scores.dPercentile - formalityTarget);
  const objectivityDiff = Math.abs(scores.ePercentile - objectivityTarget);

  // Ultra-aggressive scoring function - dual zone approach
  const calculateUltraAggressiveMatch = (difference: number): number => {
    // Perfect match gets 100%
    if (difference === 0) return 100;
    
    // Zone 1: 0-10 point difference - generous scoring with minimal penalty
    if (difference <= 10) {
      // Use a gentle curve for small differences: 100 * (1 - (diff/50)^1.5)
      // This keeps scores very high for small differences
      const normalizedDiff = difference / 50;
      const score = 100 * (1 - Math.pow(normalizedDiff, 1.5));
      return Math.max(85, score); // Minimum 85% for differences <= 10
    }
    
    // Zone 2: 10+ point difference - harsh exponential penalty
    if (difference <= 25) {
      // Steep drop for medium differences: exponential curve with power 3.0
      const normalizedDiff = (difference - 10) / 40; // Normalize 10-25 range to 0-0.375
      const score = 85 * Math.pow(1 - normalizedDiff, 3.0);
      return Math.max(25, score); // Floor at 25% for medium differences
    }
    
    // Zone 3: 25+ point difference - very harsh penalty
    if (difference <= 50) {
      // Dramatic drop for large differences
      const normalizedDiff = (difference - 25) / 25; // Normalize 25-50 range to 0-1
      const score = 25 * Math.pow(1 - normalizedDiff, 4.0);
      return Math.max(5, score); // Floor at 5% for large differences
    }
    
    // Zone 4: 50+ point difference - near zero scores
    // For differences > 50, score approaches 0
    const normalizedDiff = Math.min((difference - 50) / 50, 1); // Cap at 1
    const score = 5 * Math.pow(1 - normalizedDiff, 5.0);
    return Math.max(0, score);
  };

  // Calculate ultra-aggressive match percentages for each trait
  const dominanceMatch = calculateUltraAggressiveMatch(dominanceDiff);
  const extraversionMatch = calculateUltraAggressiveMatch(extraversionDiff);
  const patienceMatch = calculateUltraAggressiveMatch(patienceDiff);
  const formalityMatch = calculateUltraAggressiveMatch(formalityDiff);
  const objectivityMatch = calculateUltraAggressiveMatch(objectivityDiff);

  // Calculate average match percentage
  const averageMatch = (dominanceMatch + extraversionMatch + patienceMatch + formalityMatch + objectivityMatch) / 5;

  return Math.round(averageMatch);
};

export const getMatchColor = (matchPercentage: number): string => {
  // Adjusted thresholds for more aggressive scoring
  if (matchPercentage >= 90) return 'text-green-600 bg-green-50 border-green-200';
  if (matchPercentage >= 75) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  if (matchPercentage >= 50) return 'text-orange-600 bg-orange-50 border-orange-200';
  return 'text-red-600 bg-red-50 border-red-200';
};

export const getMatchLabel = (matchPercentage: number): string => {
  // Adjusted labels for more aggressive scoring
  if (matchPercentage >= 90) return 'Excellent Match';
  if (matchPercentage >= 75) return 'Good Match';
  if (matchPercentage >= 50) return 'Fair Match';
  return 'Poor Match';
};
